import React, { useState } from "react";
import styled from "@emotion/styled";

// import { Typography } from "../Typography/Typography";
import { Card, CardHeader } from "../Card/Card";
import { Button } from "../Button";
import * as tokens from "../../design-tokens";

import { FileSvg } from "../../assets/icons/set/FileSvg";
import { DownloadSvg } from "../../assets/icons/set/DownloadSvg";
import { TrashSvg } from "../../assets/icons/set/TrashSvg";
import { UploadSection } from "./UploadSection";

const UPLOAD_CARD_TEST_IDS = {
  editButton: "UploadCard-button-edit",
  downloadButton: "UploadCard-button-download",
  previewButton: "UploadCard-button-preview",
  deleteButton: "UploadCard-button-delete",
};

const UploadCard = <T,>({
  title,
  files,
  accept,
  disabled,
  multiple,
  maxFileSizeInMB,
  getFileNameField,
  getFileIdField,
  onEdit,
  onError,
  onSelect,
  onDelete,
  onDownload,
  onPreview,
}: UploadCardProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const buttonText = isEditing ? "Close" : "Edit";
  const hasFiles = files?.length !== 0;

  return (
    <Card>
      <CardHeader
        title={title}
        rightContent={
          hasFiles && (
            <Button
              customTestId={UPLOAD_CARD_TEST_IDS.editButton}
              color="link"
              onClick={() => {
                onEdit?.();
                setIsEditing(!isEditing);
              }}
            >
              {buttonText}
            </Button>
          )
        }
      />
      {files?.map((file) => (
        <FileRow key={`${getFileIdField(file)}`}>
          <RowInnerContainer gap={8}>
            <FileSvg width={24} height={24} color={tokens.BRAND_HEROBLUE} />
            <StyledLinkButton
              color="link"
              onClick={() => onPreview(file)}
              customTestId={UPLOAD_CARD_TEST_IDS.previewButton}
            >
              {getFileNameField(file)}
            </StyledLinkButton>
          </RowInnerContainer>
          <RowInnerContainer gap={16}>
            <Button
              color="tertiary"
              onClick={() => onDownload(file)}
              customTestId={UPLOAD_CARD_TEST_IDS.downloadButton}
              iconMiddle={
                <DownloadSvg
                  width={24}
                  height={24}
                  color={tokens.BRAND_HEROBLUE}
                />
              }
              style={{ padding: 0 }}
            />

            {isEditing && (
              <Button
                color="tertiary"
                onClick={() => onDelete(file)}
                customTestId={UPLOAD_CARD_TEST_IDS.deleteButton}
                iconMiddle={
                  <TrashSvg width={24} height={24} color={tokens.ERROR_30} />
                }
                style={{ padding: 0 }}
              />
            )}
          </RowInnerContainer>
        </FileRow>
      ))}
      <UploadSection
        onError={onError}
        onSelect={onSelect}
        accept={accept}
        multiple={multiple}
        maxFileSizeInMB={maxFileSizeInMB}
        disabled={disabled}
        text="Upload monthly utility bill(s) for review."
      />
    </Card>
  );
};

const StyledLinkButton = styled(Button)`
  &.sr-button {
    font-size: 20px;
  }
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RowInnerContainer = styled.div<{ gap: number }>`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.gap}px;
`;

interface UploadCardProps<T> {
  title: string;
  files: T[];
  multiple?: boolean;
  accept?: string;
  maxFileSizeInMB?: number;
  isUploading?: boolean;
  disabled?: boolean;
  getFileNameField: (file: T) => string;
  getFileIdField: (file: T) => string | number;
  onEdit?: () => void;
  onError: (e: Error) => void;
  onSelect: (selectedFiles: FileList) => void;
  onDelete: (file: T) => void;
  onDownload: (file: T) => void;
  onPreview: (file: T) => void;
}

export type { UploadCardProps };
export { UploadCard, UPLOAD_CARD_TEST_IDS };
